import { useFetcher } from "~/hooks/useFetcher";
import {
  SKILL_LIST_LIMIT,
  skillsListPath,
  type PartialSkill,
  type SkillPage,
} from "~/services/skills";

interface UseUserSkillsResult {
  skills: PartialSkill[];
  isLoading: boolean;
}

// Lists the USER-scope skills owned by the authenticated caller — the personal
// skill library shown in the profile page's Skills section. Mutations go
// through `useSkillMutations`, which invalidates this same key on write.
//
// Uses the legacy `scope=USER` filter rather than the multi-scope `user_id`
// filter: the multi-scope path resolves USER-only requests through
// `_list_legacy`'s default branch, which unions in SYSTEM skills too.
// `scope=USER` has its own dedicated branch that returns USER rows only, and
// resolves ownership from the caller's auth token server-side, so no email
// needs to be sent at all. `email` here just gates the request until the user
// context has loaded.
export function useUserSkills(email: string | undefined): UseUserSkillsResult {
  const key = email ? skillsListPath({ scope: "USER", limit: SKILL_LIST_LIMIT }) : null;
  const { data, isLoading } = useFetcher<SkillPage>(key);
  const skills = data?.items ?? [];

  // The gated window is still loading as far as the caller is concerned —
  // SWR reports a null key as settled, which reads as an empty skill library.
  return { skills, isLoading: !email || isLoading };
}
